import type { StatId } from '../sim/race/types';
import { el, formatMoney } from './dom';

const STAT_SHORT: Record<StatId, string> = {
  speed: 'SPD',
  stamina: 'STA',
  accel: 'ACC',
  heart: 'HRT',
};

const LEAGUE_NAMES = ['Local', 'Regional', 'National', 'Grand Derby'];

function ordinal(place: number): string {
  const suffix = place === 1 ? 'st' : place === 2 ? 'nd' : place === 3 ? 'rd' : 'th';
  return `${place}${suffix}`;
}

/** Everything worth telling the player about the day just finished. */
export interface DaySummary {
  /** The day that just ended. */
  day: number;
  dayEnd: number;
  newDay: number;
  season: number;
  seasonDay: number;
  /** XP earned per stat across the day's sessions. */
  xp: Partial<Record<StatId, number>>;
  sessions: number;
  levelUps: { stat: StatId; level: number }[];
  races: { league: number; place: number; purseCents: number; dnf: boolean; horseName: string }[];
  /** Overnight happenings: foals, retirements, the turn of the season. */
  notes: string[];
  feedName: string;
  feedCostCents: number;
  energy: number;
  moneyCents: number;
  moneyDeltaCents: number;
  /** Today's card, if the player is entered. */
  raceToday: { league: number; className: string; distanceM: number; going: string } | null;
  nextRaceDay: number | null;
  horseName: string;
  injuryDays: number;
}

/** The day's-end report: what the stable did, and what tomorrow holds. */
export function showDaySummary(
  root: HTMLElement,
  summary: DaySummary,
  onClose: () => void,
): () => void {
  const rows: HTMLElement[] = [];

  const xpEntries = (Object.entries(summary.xp) as [StatId, number][]).filter(
    ([, amount]) => amount > 0,
  );
  if (xpEntries.length > 0) {
    rows.push(
      el(
        'div',
        { class: 'summary-row' },
        el('span', { class: 'summary-label' }, `Training (${summary.sessions})`),
        el(
          'span',
          { class: 'summary-value good' },
          xpEntries.map(([stat, amount]) => `${STAT_SHORT[stat]} +${amount}`).join(' · '),
        ),
      ),
    );
  }
  for (const levelUp of summary.levelUps) {
    rows.push(
      el(
        'div',
        { class: 'summary-row' },
        el('span', { class: 'summary-label' }, 'Level up!'),
        el('span', { class: 'summary-value good' }, `${STAT_SHORT[levelUp.stat]} is now ${levelUp.level}`),
      ),
    );
  }
  for (const race of summary.races) {
    const { league, place, purseCents, dnf, horseName } = race;
    rows.push(
      el(
        'div',
        { class: 'summary-row' },
        el('span', { class: 'summary-label' }, `${horseName} · ${LEAGUE_NAMES[league] ?? 'Race'}`),
        el(
          'span',
          { class: `summary-value ${place === 1 ? 'good' : dnf ? 'bad' : ''}` },
          dnf
            ? 'did not finish'
            : `${ordinal(place)}${purseCents > 0 ? ` · ${formatMoney(purseCents)}` : ''}`,
        ),
      ),
    );
  }
  if (rows.length === 0) {
    rows.push(el('div', { class: 'summary-quiet' }, 'A quiet day around the yard.'));
  }

  for (const note of summary.notes) {
    rows.push(el('div', { class: 'summary-note' }, note));
  }

  const ledger = el(
    'div',
    { class: 'summary-row ledger' },
    el('span', { class: 'summary-label' }, 'Books'),
    el(
      'span',
      { class: `summary-value ${summary.moneyDeltaCents >= 0 ? 'good' : 'bad'}` },
      `${summary.moneyDeltaCents >= 0 ? '+' : '−'}${formatMoney(Math.abs(summary.moneyDeltaCents))} · ${formatMoney(summary.moneyCents)} on hand`,
    ),
  );

  const tomorrow: HTMLElement[] = [
    el(
      'div',
      { class: 'summary-row' },
      el('span', { class: 'summary-label' }, 'Feed'),
      el(
        'span',
        { class: 'summary-value' },
        `${summary.feedName}${summary.feedCostCents > 0 ? ` −${formatMoney(summary.feedCostCents)}` : ' (free)'} · ⚡${summary.energy}`,
      ),
    ),
  ];
  if (summary.injuryDays > 0) {
    tomorrow.push(
      el(
        'div',
        { class: 'summary-row' },
        el('span', { class: 'summary-label' }, 'Vet'),
        el(
          'span',
          { class: 'summary-value bad' },
          `${summary.horseName} needs ${summary.injuryDays} more day${summary.injuryDays === 1 ? '' : 's'} of rest`,
        ),
      ),
    );
  }
  if (summary.raceToday) {
    const card = summary.raceToday;
    tomorrow.push(
      el(
        'div',
        { class: 'summary-row' },
        el('span', { class: 'summary-label urgent' }, 'RACE DAY'),
        el(
          'span',
          { class: 'summary-value urgent' },
          `${LEAGUE_NAMES[card.league] ?? ''} ${card.className} · ${card.distanceM}m · ${card.going}`,
        ),
      ),
    );
  } else if (summary.nextRaceDay !== null) {
    tomorrow.push(
      el(
        'div',
        { class: 'summary-row' },
        el('span', { class: 'summary-label' }, 'Next race'),
        el('span', { class: 'summary-value' }, `day ${summary.nextRaceDay}`),
      ),
    );
  }

  const panel = el(
    'div',
    { class: 'panel modal day-summary' },
    el(
      'div',
      { class: 'modal-title' },
      summary.dayEnd > summary.day
        ? `Days ${summary.day}–${summary.dayEnd} — stable report`
        : `Day ${summary.day} — day's end`,
    ),
    el('div', { class: 'modal-sub' }, `Season ${summary.season}`),
    ...rows,
    ledger,
    el('div', { class: 'modal-section' }, `DAY ${summary.newDay} · season day ${summary.seasonDay}/12`),
    ...tomorrow,
    el(
      'div',
      { class: 'modal-foot' },
      el('button', { class: 'button primary', onclick: () => onClose() }, 'Get to work (Enter)'),
    ),
  );

  const backdrop = el('div', { class: 'modal-backdrop' }, panel);
  backdrop.addEventListener('pointerdown', (event) => {
    if (event.target === backdrop) onClose();
  });
  root.append(backdrop);
  return () => backdrop.remove();
}
